import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Calendar, Clock, MapPin, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useEvents, FamilyEvent } from '@/hooks/useEvents';
import { useFamily } from '@/hooks/useFamily';
import { PersonName } from '@/components/PersonName';
import { LocationInput } from '@/components/ui/location-input';
import { MapView } from '@/components/ui/map-view';
import { ShareableEventButton } from '@/components/ShareableEventButton';
import { usePublicEventPages } from '@/hooks/usePublicEventPages';
import { EventTaskSection } from '@/components/events/EventTaskSection';

export default function EventDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { family, familyMembers } = useFamily();
  const { events, loading } = useEvents(family?.id);
  const { getEventRSVPs } = usePublicEventPages();
  const [event, setEvent] = useState<FamilyEvent | null>(null);
  const [eventRSVPs, setEventRSVPs] = useState<any[]>([]);

  useEffect(() => {
    if (events && id) {
      const foundEvent = events.find(e => e.id === id);
      if (foundEvent) {
        setEvent(foundEvent);
        // Charger les RSVP pour cet événement
        loadEventRSVPs(foundEvent.id);
      }
    }
  }, [events, id]);

  const loadEventRSVPs = async (eventId: string) => {
    try {
      const rsvps = await getEventRSVPs(eventId);
      setEventRSVPs(rsvps || []);
    } catch (error) {
      console.error('Erreur lors du chargement des RSVP:', error);
      setEventRSVPs([]);
    }
  };

  const translateRecurrencePattern = (pattern: string) => {
    switch (pattern) {
      case 'daily':
        return 'Tous les jours';
      case 'weekdays':
        return 'Tous les jours ouvrés';
      case 'weekly':
        return 'Toutes les semaines';
      case 'monthly':
        return 'Tous les mois';
      case 'yearly':
        return 'Tous les ans';
      default:
        return 'Événement récurrent';
    }
  };

  const isAllDayEvent = (startDate: Date, endDate: Date) => {
    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();
    const endHour = endDate.getHours();
    const endMinute = endDate.getMinutes();
    
    return startHour === 0 && startMinute === 0 && endHour === 23 && endMinute === 59;
  };

  const getEventAttendees = () => {
    // Retourner les RSVP de la page publique
    return eventRSVPs.filter(rsvp => rsvp.status === 'attending').map(rsvp => ({
      id: rsvp.id,
      first_name: rsvp.first_name,
      last_name: rsvp.last_name,
      email: rsvp.email,
      phone: rsvp.phone,
      message: rsvp.message,
      avatar_url: null, // Les RSVP publics n'ont pas d'avatar
      guest_count: rsvp.guest_count || 1
    }));
  };

  const getCreator = () => {
    if (!event || !familyMembers) return null;
    return familyMembers.find(member => member.profile_id === event.created_by);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-page-gradient">
        <div className="flex items-center px-4 pt-2 pb-4 bg-background">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/calendar')}
            className="hover:bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold ml-3">Événement introuvable</h1>
        </div>
        <div className="container mx-auto px-4">
          <Alert>
            <AlertDescription>
              Cet événement n'existe pas ou a été supprimé.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const creator = getCreator();
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);

  return (
    <div className="min-h-screen bg-page-gradient">
      {/* Header - Same design as TaskListDetailView */}
      <div className="flex items-center px-4 pt-2 pb-4 bg-background">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/calendar')}
          className="hover:bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        
        <div className="flex items-center justify-between ml-3 flex-1">
          <h1 className="text-lg font-semibold">{event.title}</h1>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          aria-label="Modifier l'événement"
          onClick={() => navigate(`/events/edit/${event.id}`)}
          className="hover:bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <Pencil className="h-4 w-4 text-foreground" />
        </Button>
      </div>

      {/* Event Details */}
      <div className="container mx-auto px-4 space-y-6">
        <div className="space-y-6">
          <div>
            <p className="text-sm font-medium mb-2">Description</p>
            <p className="text-muted-foreground">
              {event.description || "Aucune description"}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Date et heure</p>
            <div className="space-y-1">
              <p className="text-muted-foreground">
                {startDate.toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
              {!isAllDayEvent(startDate, endDate) && (
                <p className="text-muted-foreground">
                  {startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>

          {event.location && (
            <div className="space-y-3">
              <p className="text-sm font-medium mb-2">Lieu</p>
              <p className="text-muted-foreground">{event.location}</p>
              {event.latitude && event.longitude && (
                <MapView 
                  latitude={event.latitude}
                  longitude={event.longitude}
                  address={event.location}
                  height="300px"
                  className="w-full"
                />
              )}
            </div>
          )}

          {event.is_recurring && event.recurrence_pattern && (
            <div>
              <p className="text-sm font-medium mb-2">Récurrence</p>
              <p className="text-muted-foreground">
                {translateRecurrencePattern(event.recurrence_pattern)}
              </p>
            </div>
          )}

          <div>
          </div>

          <div>
            <p className="text-sm font-medium mb-3">Participants</p>
            <div className="space-y-4">
              {getEventAttendees().map((attendee) => (
                <div key={attendee.id} className="border-l-2 border-primary/20 pl-3">
                  <p className="font-medium text-sm">{attendee.first_name} {attendee.last_name}</p>
                  <div className="mt-1 space-y-1 text-xs text-muted-foreground">
                    {attendee.email && (
                      <p>📧 {attendee.email}</p>
                    )}
                    {attendee.phone && (
                      <p>📞 {attendee.phone}</p>
                    )}
                    <p>👥 {attendee.guest_count} personne{attendee.guest_count > 1 ? 's' : ''}</p>
                    {attendee.message && (
                      <p className="italic">💬 {attendee.message}</p>
                    )}
                  </div>
                </div>
              ))}
              {getEventAttendees().length === 0 && (
                <p className="text-muted-foreground">Aucun participant pour le moment</p>
              )}
            </div>
            {eventRSVPs.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="text-sm font-medium mb-2">Résumé des réponses</div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>✅ {eventRSVPs.filter(r => r.status === 'attending').length} participant{eventRSVPs.filter(r => r.status === 'attending').length > 1 ? 's' : ''}</p>
                    {eventRSVPs.filter(r => r.status === 'maybe').length > 0 && (
                      <p>🤔 {eventRSVPs.filter(r => r.status === 'maybe').length} peut-être</p>
                    )}
                    {eventRSVPs.filter(r => r.status === 'not_attending').length > 0 && (
                      <p>❌ {eventRSVPs.filter(r => r.status === 'not_attending').length} ne vien{eventRSVPs.filter(r => r.status === 'not_attending').length > 1 ? 'nent' : 't'} pas</p>
                    )}
                    <div className="pt-2 border-t border-border/50">
                      <p className="font-medium text-foreground">
                        🎉 Total: {getEventAttendees().reduce((total, attendee) => total + attendee.guest_count, 0)} personne{getEventAttendees().reduce((total, attendee) => total + attendee.guest_count, 0) > 1 ? 's' : ''} confirmée{getEventAttendees().reduce((total, attendee) => total + attendee.guest_count, 0) > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section des tâches liées à l'événement */}
          {family?.id && event && (
            <EventTaskSection 
              event={event}
              familyId={family.id}
            />
          )}

          <div className="pt-4 border-t">
            <ShareableEventButton eventId={event.id} />
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Créez une page publique pour permettre aux invités de confirmer leur présence et partager leurs coordonnées
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}